import isNull from "lodash/isNull";
import Stripe from "stripe";
import {
  confirmPasswordReset,
  sendPasswordResetEmail,
  signInWithCustomToken,
} from "firebase/auth";

import { bugNotif } from "../api/bugsnag";
import {
  auth,
  db,
  fbProvider,
  googleProvider,
  signInWithPopup,
} from "../firebase";
import { doc, getDoc, onSnapshot } from "../../Common/utils/firebaseFunctions";
import { User as WLUser } from "../../Common/models/user";
import {
  getErrorMsg,
  hashStringToSHA256,
  isMobile,
} from "../../Common/utils/common";
import { getSubscription } from "./subscription";
import { ViewWLSubscriptionsTiers } from "../../Common/models/subscriptions";
import { ttqTrackEvent } from "../api/tiktokEvents";
import { getStripePriceValueWithPromo } from "./promo";
import {
  getUserHashedDetails,
  getUserTokenRequest,
  trackWebSignUpSteps,
  validateSignInWeb,
  trackWebDownloadRates,
} from "../api/user";
import * as capacitorStorage from "../../Common/utils/localStorage";
import { preserveWebLogRecordSessionFlag } from "../utils/webLogRocketRecord";
import {
  AnalyticsWebDownloadRateParams,
  WebSignUpParamsAnalytics,
} from "../../Common/models/analytics";
import { isEmpty } from "lodash";
import { updateWithTransaction } from "./util";
import { LOCAL_STORAGE, ENVIRONMENTS } from "../../Common/config";
import { markABTestAsConverted, recordABTest } from "../api/abTest";
import { WLAbTestPayload } from "../../Common/models/config";
import { ABSignUpMetaData } from "../../Common/models/abTest";
import { USER } from "../../Common/constants/dbCollections";

/**
 * Helper function to get device info for analytics tracking
 */
// const getDeviceInfoForAnalytics = async () => {
//   try {
//     const response = await getClientIpAndUserAgent();
//     return {
//       ipAddress: response.data.ipAddress,
//       userAgent: response.data.userAgent,
//     };
//   } catch (error) {
//     console.log("Error getting device info for analytics:", error);
//     return {
//       ipAddress: null,
//       userAgent: null,
//     };
//   }
// };

export const signUpWithGoogle = async () => {
  const isMobileWeb = isMobile(true);
  const result = await (isMobileWeb ? signInWithPopup : signInWithPopup)(
    auth,
    googleProvider
  );
  // const credential = GoogleAuthProvider.credentialFromResult(result);
  // const token = credential?.accessToken;
  const user = result.user;

  console.log("User Info:", user);
  return user;
};

export const signUpWithFacebook = async () => {
  const isMobileWeb = isMobile(true);
  const result = await (isMobileWeb ? signInWithPopup : signInWithPopup)(
    auth,
    fbProvider
  );

  // const credential = GoogleAuthProvider.credentialFromResult(result);
  // const token = credential?.accessToken;
  const user = result.user;

  console.log("User Info:", user);
  return user;
};

export const sendResetPassword = async (email: string) => {
  return await sendPasswordResetEmail(auth, email);
};

export const confirmResetPassword = async (code: string, password: string) => {
  await confirmPasswordReset(auth, code, password);
};

export const trackWebSignUpStep = async (data: WebSignUpParamsAnalytics) => {
  try {
    await trackWebSignUpSteps({
      ...data,
    });
    console.log("sent --", JSON.stringify(data, null, 1));
  } catch (error) {
    console.log("silent error tracking sign up step");
    bugNotif("trackWebSignUpSteps", getErrorMsg(error).rawErrorMsg);
  }
};

export const trackWebDownloadRate = async (
  data: AnalyticsWebDownloadRateParams
) => {
  try {
    await trackWebDownloadRates({
      ...data,
    });
  } catch (error) {
    console.log("silent error tracking web download rate step");
    bugNotif("trackWebDownloadRates", getErrorMsg(error).rawErrorMsg);
  }
};

export const signInTemporaryToken = async (data: { userId: string }) => {
  try {
    const { userId } = data;
    const response = await getUserTokenRequest({ userId });
    if (response.data.valid && response.data.token) {
      const respAuthUser = await signInWithCustomToken(
        auth,
        response.data.token
      );
      const idToken = await respAuthUser.user.getIdToken();
      await capacitorStorage.setItem(LOCAL_STORAGE.fbIdToken, idToken);
      await capacitorStorage.setItem(
        LOCAL_STORAGE.userId,
        auth.currentUser?.uid!
      );
      return auth.currentUser;
    }
  } catch (error) {
    const errMsg = error as any;
    bugNotif("signInTemporaryToken", errMsg);
  }
};

export const validateSignInUser = async (data: { userId: string }) => {
  try {
    const { userId } = data;
    const response = await validateSignInWeb({ userId });
    // console.log("response -- ", response);
    if (response.data.valid && response.data.token) {
      const respAuthUser = await signInWithCustomToken(
        auth,
        response.data.token
      );
      const idToken = await respAuthUser.user.getIdToken();
      await capacitorStorage.setItem(LOCAL_STORAGE.fbIdToken, idToken);
      await capacitorStorage.setItem(
        LOCAL_STORAGE.userId,
        auth.currentUser?.uid!
      );
      return auth.currentUser;
    }

    return {
      valid: response.data.valid,
      ...(response.data.phoneNumber && {
        phoneNumber: response.data.phoneNumber,
        ...(response.data.firstName?.trim()
          ? { firstName: response.data.firstName.trim() }
          : {}),
      }),
    };
  } catch (error) {
    const errMsg = error as any;
    console.log("errMsg -- ", errMsg);
    bugNotif("validateSignInUser", errMsg);
    if ((error as any)?.response?.status === 400) {
      return { valid: false };
    }
  }
};

export const signOut = async () => {
  try {
    if (!auth.currentUser) return;
    // await clearUserTemporaryToken({ userId: auth.currentUser.uid });
    await auth.signOut();
    await capacitorStorage.removeItem(LOCAL_STORAGE.userId);
    await capacitorStorage.removeItem(LOCAL_STORAGE.fbIdToken);
    preserveWebLogRecordSessionFlag();
    localStorage.clear();
  } catch (error) {
    bugNotif("signOut -", getErrorMsg(error).rawErrorMsg);
  }
};

export const onUserDataChange = (
  userId: string,
  callback: (user: WLUser, error?: string) => void
) => {
  if (!isEmpty(userId)) {
    try {
      const unsubscribe = onSnapshot(
        doc(db, USER, userId),
        (data) => {
          // console.log("data change --- ", data.data());
          if (data.exists()) {
            const user = {
              ...data.data(),
              docId: data.id,
            } as WLUser;
            callback(user);
          } else {
            callback({} as WLUser);
          }
        },
        (error) => {
          console.log("got error --");
          callback({} as WLUser, error.message);
          bugNotif("onUserDataChange - snapshot", error.message);
        }
      );

      return unsubscribe;
    } catch (errorUnknown) {
      const error = errorUnknown as any;
      callback({} as WLUser, error.message || error);
      bugNotif("onUserDataChange", error.message || error);
    }
  } else {
    callback({} as WLUser, "Error: No user id provided.");
  }
  return () => {};
};

export const addCancelRemarks = async ({
  user,
  cancelReason,
  cancelOtherRemarks,
}: {
  user: WLUser;
  cancelReason: string;
  cancelOtherRemarks: string;
}) => {
  await updateWithTransaction(
    user.docId,
    {
      cancelReason,
      cancelOtherRemarks,
      cancelDt: new Date().valueOf(),
      updatedDt: new Date().valueOf(),
    },
    USER
  );
};

export const getUser = async (overrideId = null as null | string) => {
  const currentUser = auth.currentUser;
  if (isNull(currentUser) && isNull(overrideId)) {
    console.log("no current user");
    return null;
  }

  const userQuery = await getDoc(doc(db, USER, overrideId || currentUser!.uid));
  if (!userQuery.exists()) {
    return null;
  }

  const user = userQuery.data() as WLUser;
  return {
    ...user,
    docId: userQuery.id,
  } as WLUser;
};

export const recordSignUpABTest = async (
  data: WLAbTestPayload<ABSignUpMetaData>
) => {
  try {
    await recordABTest({
      ...data,
    });
  } catch (error) {
    bugNotif("recordSignUpABTest", getErrorMsg(error).rawErrorMsg);
  }
};

export const markSignUpABTestAsConverted = async (data: WLAbTestPayload) => {
  try {
    await markABTestAsConverted({
      ...data,
    });
  } catch (error) {
    bugNotif("markSignUpABTestAsConverted", getErrorMsg(error).rawErrorMsg);
  }
};
